import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { formatDateAsDayMonthYear } from '@/utils/dateFormatter';
import { titleize } from '@/utils/helpers';
import { formatAsInt, formatTwoDecimals } from '@/utils/numberFormatter';
import {
  renderAssetTransactionCell,
  renderCompanyTransactionCell,
  renderDealCell,
  renderDealDetailsCell,
} from './helpers';

export const TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const hoverStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const TABLE_COLUMNS = [
  {
    field: 'expanded',
    headerName: '',
    width: 25,
    filterable: false,
    hideable: false,
    renderCell: cell => {
      if (!cell.row.isGroup) return null;
      if (cell.row?.isLoadMore) return null;

      // Render the expand icon if there are children
      return (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          {cell.row.isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </Box>
      );
    },
  },
  {
    field: 'countryName',
    headerName: 'Country',
    width: 200,
    filterable: false,
    hideable: false,
    renderCell: cell => {
      if (cell.row?.isLoadMore) {
        return (
          <Button variant='contained' color='secondary'>
            Load More
          </Button>
        );
      }

      if (cell.row?.isLoading) {
        return (
          <Box display='flex' justifyContent='start' alignItems='center' height='100%'>
            <CircularProgress size={24} color='secondary' />
          </Box>
        );
      }

      if (cell.row?.isDeal) {
        return cell.row?.dealCountryName || 'Multiple Countries';
      }

      // If the row is a transaction, return the country name from the asset transaction
      return cell.row?.country?.displayName;
    },
  },
  {
    field: 'regionName',
    headerName: 'Region',
    width: 200,
    filterable: false,
    renderCell: cell => {
      if (cell.row?.isLoadMore || cell.row?.isLoading) return null;
      if (cell.row?.isDeal) {
        return cell.row?.dealRegionName || 'Multiple Regions';
      }

      if (cell)
        // If the row is a transaction, return the region name from the asset transaction
        return cell.row?.country?.region?.displayName;
    },
  },
  {
    field: 'assetName',
    headerName: 'Asset/Block Name',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        transactionField: 'assetName',
        dealField: 'dealAssetName',
        defaultValue: 'Multiple Assets',
      }),
  },
  {
    field: 'dealType',
    headerName: 'Deal Type',
    width: 200,
    filterable: false,
    renderCell: cell => renderDealCell({ cell, field: 'dealType' }),
  },
  {
    field: 'buyerCompanyName',
    headerName: 'Buyer',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderCompanyTransactionCell({
        cell,
        dealField: 'dealBuyerCompanyName',
        transactionField: 'maCompanyTransaction.buyerCompanyName',
        defaultValue: 'Multiple Buyers',
      }),
  },
  {
    field: 'sellerCompanyName',
    headerName: 'Seller',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderCompanyTransactionCell({
        cell,
        dealField: 'dealSellerCompanyName',
        transactionField: 'maCompanyTransaction.sellerCompanyName',
        defaultValue: 'Multiple Sellers',
      }),
  },
  {
    field: 'dealStatus',
    headerName: 'Deal Status',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({ cell, field: 'maDealDetails.dealStatus', formatter: titleize }),
  },
  {
    field: 'workingInterest',
    headerName: 'Stake Transacted (%)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterest',
        transactionField: 'workingInterest',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'sellerMotivation',
    headerName: 'Seller Motivation',
    width: 200,
    filterable: false,
    renderCell: cell => renderDealDetailsCell({ cell, field: 'maDealDetails.sellerMotivation' }),
  },
  {
    field: 'comment',
    headerName: 'Comment',
    width: 400,
    filterable: false,
    renderCell: cell =>
      renderDealCell({
        cell,
        field: 'comment',
        Wrapper: ({ children }) => (
          <Tooltip title={children} placement='bottom-start'>
            <span style={hoverStyles}>{children}</span>
          </Tooltip>
        ),
      }),
  },
  {
    field: 'shoreStatus',
    headerName: 'Shore Status',
    width: 200,
    filterable: false,
    sortable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealShoreStatus',
        transactionField: 'shoreStatus',
        defaultValue: 'Multiple Shore Statuses',
      }),
  },
  {
    field: 'acreage',
    headerName: 'Acreage',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.acreage',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'announcementDate',
    headerName: 'Announcement Date',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.announcementDate',
        formatter: formatDateAsDayMonthYear,
      }),
  },
  {
    field: 'effectiveDate',
    headerName: 'Effective Date',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealCell({ cell, field: 'effectiveDate', formatter: formatDateAsDayMonthYear }),
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.closingDate',
        formatter: formatDateAsDayMonthYear,
      }),
  },
  {
    field: 'reportedConsiderationBaseCashMmusd',
    headerName: 'Reported Consideration Base Cash (US$MM)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedConsiderationBaseCashMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedConsiderationBaseEquityMmusd',
    headerName: 'Reported Consideration Base Equity (US$MM)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedConsiderationBaseEquityMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedConsiderationContingentMmusd',
    headerName: 'Reported Consideration Contingent (US$MM)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedConsiderationContingentMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedDebtMmusd',
    headerName: 'Reported Debt (US$MM)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedDebtMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedCostCarryMmusd',
    headerName: 'Reported Cost Carry (US$MM)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedCostCarryMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedConsiderationTotalMmusd',
    headerName: 'Reported Consideration Total (US$MM)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedConsiderationTotalMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedConsiderationComment',
    headerName: 'Reported Consideration Comment',
    width: 300,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedConsiderationComment',
        Wrapper: ({ children }) => (
          <Tooltip title={children} placement='bottom-start'>
            <span style={hoverStyles}>{children}</span>
          </Tooltip>
        ),
      }),
  },
  {
    field: 'weaWorkingInterestValuation',
    headerName: 'WEA Transacted Valuation (US$MM)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestValuation',
        transactionField: 'weaWorkingInterestValuation',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'sellSideAdvisor',
    headerName: 'Sell Side Advisor(s)',
    width: 200,
    filterable: false,
    renderCell: cell => renderDealDetailsCell({ cell, field: 'maDealDetails.sellSideAdvisor' }),
  },
  {
    field: 'buySideAdvisor',
    headerName: 'Buy Side Advisor(s)',
    width: 200,
    filterable: false,
    renderCell: cell => renderDealDetailsCell({ cell, field: 'maDealDetails.buySideAdvisor' }),
  },
  {
    field: 'paymentType',
    headerName: 'Payment Type',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({ cell, field: 'maDealDetails.paymentType', formatter: titleize }),
  },
  {
    field: 'reportedSignatureBonusMmusd',
    headerName: 'Reported Signature Bonus (US$MM)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedSignatureBonusMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedCostCommitmentMmusd',
    headerName: 'Reported Cost Commitment (US$MM)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedCostCommitmentMmusd',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted1pReservesMmboe',
    headerName: 'Reported Transacted 1P Reserves (MMboe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted1pReservesMmboe',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted1pOilCondensateReservesMmbbl',
    headerName: 'Reported Transacted 1P Oil/Condensate Reserves (MMbbl)',
    width: 300,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted1pOilCondensateReservesMmbbl',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted1pGasReservesBcf',
    headerName: 'Reported Transacted 1P Gas Reserves (Bcf)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted1pGasReservesBcf',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2pReservesMmboe',
    headerName: 'Reported Transacted 2P Reserves (MMboe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2pReservesMmboe',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2pOilCondensateReservesMmbbl',
    headerName: 'Reported Transacted 2P Oil/Condensate Reserves (MMbbl)',
    width: 300,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2pOilCondensateReservesMmbbl',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2pGasReservesBcf',
    headerName: 'Reported Transacted 2P Gas Reserves (Bcf)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2pGasReservesBcf',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2cResourceMmboe',
    headerName: 'Reported Transacted 2C Resource (MMboe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2cResourceMmboe',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2cOilCondensateResourceMmbbl',
    headerName: 'Reported Transacted 2C Oil/Condensate Resource (MMbbl)',
    width: 300,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2cOilCondensateResourceMmbbl',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedTransacted2cGasResourceBcf',
    headerName: 'Reported Transacted 2C Gas Resource (Bcf)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedTransacted2cGasResourceBcf',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedProductionBoed',
    headerName: 'Reported Production (boe/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedProductionBoed',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'reportedOilCondensateProductionBbld',
    headerName: 'Reported Oil/Condensate Production (bbl/d)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedOilCondensateProductionBbld',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'reportedGasProductionMmcfd',
    headerName: 'Reported Gas Production (MMcf/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedGasProductionMmcfd',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'weaWorkingInterestReserves',
    headerName: 'WEA Transacted Reserves (MMboe)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestReserves',
        transactionField: 'weaWorkingInterestReserves',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'weaWorkingInterestLiquidsReserves',
    headerName: 'WEA Transacted Oil/Condensate Reserves (MMbbl)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestLiquidsReserves',
        transactionField: 'weaWorkingInterestLiquidsReserves',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'weaWorkingInterestGasReserves',
    headerName: 'WEA Transacted Gas Reserves (Bcf)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestGasReserves',
        transactionField: 'weaWorkingInterestGasReserves',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'weaWorkingInterestProduction',
    headerName: 'WEA Transacted Production (boe/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestProduction',
        transactionField: 'weaWorkingInterestProduction',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'weaWorkingInterestLiquidsProduction',
    headerName: 'WEA Transacted Oil/Condensate Production (bbl/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestLiquidsProduction',
        transactionField: 'weaWorkingInterestLiquidsProduction',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'weaWorkingInterestGasProduction',
    headerName: 'WEA Transacted Gas Production (MMcf/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealWorkingInterestGasProduction',
        transactionField: 'weaWorkingInterestGasProduction',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'reportedReservesAcquisitionCostUsdPerBoe',
    headerName: 'Reported Reserves Acquisition Cost ($/boe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedReservesAcquisitionCostUsdPerBoe',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedPricePerFlowingBarrelUsdPerBoed',
    headerName: 'Reported Price per Flowing Barrel ($/boe/d)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedPricePerFlowingBarrelUsdPerBoed',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'reportedAcreageAcquisitionCostUsdPerAcre',
    headerName: 'Reported Acreage Acquisition Cost ($/acre)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedAcreageAcquisitionCostUsdPerAcre',
        formatter: formatAsInt,
      }),
  },

  {
    field: 'weaReservesAcquisitionCost',
    headerName: 'WEA Reserves Acquisition Cost ($/boe)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealReservesAcquisitionCost',
        transactionField: 'weaReservesAcquisitionCost',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'weaConsiderationByReserves',
    headerName: 'Base Consideration / WEA Reserves ($/boe)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealCell({
        cell,
        field: 'dealConsiderationByReserves',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'weaPriceFlowingBarrel',
    headerName: 'WEA Price per Flowing Barrel ($/boe/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealPriceFlowingBarrel',
        transactionField: 'weaPriceFlowingBarrel',
        formatter: formatAsInt,
      }),
  },
  {
    field: 'weaConsiderationByProduction',
    headerName: 'Base Consideration / WEA Production ($/boe/d)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderDealCell({
        cell,
        field: 'dealConsiderationByProduction',
        formatter: formatAsInt,
      }),
  },

  {
    field: 'reportedRemainingEmissionsIntensityKgco2ePerBoe',
    headerName: 'Reported Remaining Emissions Intensity (kgCO2e/boe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedRemainingEmissionsIntensityKgco2ePerBoe',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'reportedCurrentEmissionsIntensityKgco2ePerBoe',
    headerName: 'Reported Current Emissions Intensity (kgCO2e/boe)',
    width: 250,
    filterable: false,
    renderCell: cell =>
      renderDealDetailsCell({
        cell,
        field: 'maDealDetails.reportedCurrentEmissionsIntensityKgco2ePerBoe',
        formatter: formatTwoDecimals,
      }),
  },

  {
    field: 'weaRemainingEmissionsIntensity',
    headerName: 'WEA Remaining Emissions Intensity (kgCO2e/boe)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealRemainingEmissionsIntensity',
        transactionField: 'weaRemainingEmissionsIntensity',
        formatter: formatTwoDecimals,
      }),
  },
  {
    field: 'weaCurrentEmissionsIntensity',
    headerName: 'WEA Current Emissions Intensity (kgCO2e/boe)',
    width: 200,
    filterable: false,
    renderCell: cell =>
      renderAssetTransactionCell({
        cell,
        dealField: 'dealCurrentEmissionsIntensity',
        transactionField: 'weaCurrentEmissionsIntensity',
        formatter: formatTwoDecimals,
      }),
  },
];
